'use client';
import {
    hasCldImage,
    isTypePageCaseStudy,
    isTypePageJournal,
    TypeButton,
    TypePageCaseStudySkeleton,
    TypePageJournalSkeleton,
} from '@fm-nx/contentful-types';
import { PageContentTypes } from '@fm-nx/constants';
import { getLinkToForPage } from '@fm-nx/navigation';
import { Icon } from '@fm-nx/svg';
import { Entry } from 'contentful';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import Button from '../button/button';
import { useStickyButton } from '../sticky-button-provider/sticky-button-provider';
import CardSquare from '../card-square/card-square';
import { formatDateLong } from '../helpers/date-utils';
import { useBreakpoint } from '../hooks/useResponsive';

export type SlideProps = {
    page:
        | (Entry<TypePageCaseStudySkeleton | TypePageJournalSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)
        | undefined;
};

const Slide = (props: SlideProps) => {
    const { page } = props;

    const overline = useMemo(() => {
        if (!page) return '';
        if (isTypePageCaseStudy(page)) {
            return 'Case Study';
        }
        if (isTypePageJournal(page)) {
            return 'Journal';
        }
        return '';
    }, [page]);

    const author = useMemo(() => {
        if (!page || !isTypePageJournal(page)) return '';

        if (page.fields.authors) {
            return page.fields.authors
                .map(
                    (author) =>
                        `${author?.fields?.person?.fields?.firstName} ${author?.fields?.person?.fields?.lastName}`
                )
                .join(' + ');
        }
        return '';
    }, [page]);

    const year = useMemo(() => {
        if (!page) return null;
        if (isTypePageCaseStudy(page)) {
            return page.fields.year ? page.fields.year : null;
        }
        return null;
    }, [page]);

    const date = useMemo(() => {
        if (!page) return null;
        if (isTypePageJournal(page)) {
            return formatDateLong(page.fields.publishDate);
        }
        return null;
    }, [page]);

    if (!page) return null;

    const { heroImage, title, summary } = page.fields;

    // case studies should use listImage and journal uses hero
    const getImageCloudinary = () => {
        if (isTypePageJournal(page)) {
            const image = hasCldImage(heroImage?.fields) ? heroImage?.fields.cloudinaryMedia[0] : null;
            return image ? `${image?.public_id}` : '';
        }
        if (isTypePageCaseStudy(page) && page.fields.listImage) {
            const { listImage } = page.fields;
            const image = hasCldImage(listImage?.fields) ? listImage?.fields.cloudinaryMedia[0] : null;
            return image ? `${image?.public_id}` : '';
        }
        if (isTypePageCaseStudy(page) && page.fields.heroImage) {
            const { heroImage } = page.fields;
            const image = hasCldImage(heroImage?.fields) ? heroImage?.fields.cloudinaryMedia[0] : null;
            return image ? `${image?.public_id}` : '';
        }
        return '';
    };

    return (
        <div>
            {/* LinkWrapper is clicking to page on slide */}
            <a href={getLinkToForPage({ page }).href} className="group">
                <CardSquare
                    overline={overline}
                    summary={summary || ''}
                    title={title}
                    imageUrl={getImageCloudinary()}
                    author={author}
                    date={date}
                    year={year}
                    imageAltText={heroImage?.fields.altText}
                    addXPadding
                />
            </a>
        </div>
    );
};

export type SlideshowProps = {
    headerText?: string;
    button?: TypeButton<'WITHOUT_UNRESOLVABLE_LINKS'>;
    overline?: string;
    pages:
        | (
              | Entry<TypePageCaseStudySkeleton | TypePageJournalSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
              | undefined
          )[]
        | undefined;
};

export const Slideshow = (props: SlideshowProps) => {
    const { isTabletBelow } = useBreakpoint();
    const { isMobile } = useBreakpoint();
    const { overline, headerText, button } = props;
    const [slider, setSlider] = useState<Slider | null>(null);
    const [showLeftCaret, setShowLeftCaret] = useState(false);
    const [showRightCaret, setShowRightCaret] = useState(false);
    const stickyCtx = useStickyButton();
    const sectionRef = useRef<HTMLDivElement>(null);
    const zoneId = useId();

    // Register this slideshow as a sticky button zone
    const [isRegistered, setIsRegistered] = useState(false);
    useEffect(() => {
        if (!stickyCtx || !button?.fields || !sectionRef.current) return;

        // Wait for sticky button to register its default config
        const checkInterval = setInterval(() => {
            if (stickyCtx.isActive) {
                clearInterval(checkInterval);
                // Pass the page reference through rather than folding a bare slug
                // into linkPath — a slug with no leading slash resolves relative
                // to the current route (e.g. /expertise/case-studies).
                const linkPage = button.fields.linkPage;
                const contentType = linkPage?.sys.contentType.sys.id;
                const normalizedContentType = contentType
                    ? contentType.charAt(0).toLowerCase() + contentType.slice(1)
                    : undefined;
                const pageType = Object.values(PageContentTypes).find(
                    (value) => value === normalizedContentType
                ) as keyof typeof PageContentTypes;

                const config = {
                    text: button.fields.buttonText?.toString() || '',
                    linkPath: button.fields.linkPath?.toString() || '#',
                    buttonType: 'outline-dark' as const,
                    slug: linkPage?.fields?.slug?.toString(),
                    pageType,
                };
                stickyCtx.registerZone({ id: zoneId, ref: sectionRef, config });
                setIsRegistered(true);
            }
        }, 100);

        return () => {
            clearInterval(checkInterval);
            stickyCtx.unregisterZone(zoneId);
            setIsRegistered(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [button, zoneId]);

    if (!props.pages) return null;

    // Rotate pages array on desktop only: move first item to last
    let pages = props.pages;
    if (pages && pages.length > 1 && !isTabletBelow) {
        pages = [...pages.slice(1), pages[0]];
    }

    const handlePrevHover = () => {
        setShowLeftCaret(true);
    };

    const handleNextHover = () => {
        setShowRightCaret(true);
    };

    const handlePrevLeave = () => {
        setShowLeftCaret(false);
    };

    const handleNextLeave = () => {
        setShowRightCaret(false);
    };

    const handlePrevClick = () => {
        slider?.slickPrev();
    };

    const handleNextClick = () => {
        slider?.slickNext();
    };

    const getConfigurableProps = () => ({
        slidesToShow: isTabletBelow ? 1 : 2,
        centerMode: true,
        centerPadding: isTabletBelow ? '20px' : '100px',
        infinite: true,
    });

    const getSlides = () => {
        if (!pages || pages.length === 0) return null;

        return pages.map((page) => (page ? <Slide key={page.sys.id} page={page} /> : null));
    };

    const hideSlideshowButton = isRegistered && !isMobile;

    return (
        <div ref={sectionRef} className="relative my-20">
            <div className="w-full mx-auto max-w-container">
                <div className="my-6">
                    <div className="block mx-6 md:mx-0 md:grid md:grid-cols-14">
                        <div className="col-span-6 col-start-2">
                            <div className="flex items-center">
                                <div className="space-y-6">
                                    {overline && <span className="overline h5">{overline}</span>}
                                    {headerText && <h2 className="h-xl">{headerText}</h2>}
                                </div>
                            </div>
                        </div>
                        {button && !hideSlideshowButton && (
                            <div className="hidden col-span-6 md:block" aria-hidden="true">
                                <div className="flex items-center justify-end">
                                    <Button button={button} buttonType="outline-dark" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {pages && pages.length > 1 && (
                    <div className="block pb-10 mx-6 md:mx-0 md:grid md:grid-cols-14">
                        <div className="col-span-6 col-start-2">
                            <div className="w-[310px] h-full bg-white">
                                <div className="grid h-full pr-14">
                                    <div className="flex items-center gap-5">
                                        <div className="flex-none">
                                            <button
                                                type="button"
                                                onClick={handlePrevClick}
                                                className="mb-0 mr-4 h5 after:bg-gray-900 border-b-in"
                                                onMouseEnter={handlePrevHover}
                                                onMouseLeave={handlePrevLeave}
                                            >
                                                Prev
                                            </button>
                                        </div>
                                        <div className="flex items-center w-full">
                                            {showLeftCaret && (
                                                <div style={{ marginRight: '-12px', height: '24px' }}>
                                                    <Icon name="caret" color={'black'} direction={'left'} />
                                                </div>
                                            )}
                                            <div className="grow">
                                                <hr className="w-full" style={{ border: '1px solid' }} />
                                            </div>
                                            {showRightCaret && (
                                                <div style={{ marginLeft: '-12px', height: '24px' }}>
                                                    <Icon name="caret" color={'black'} direction={'right'} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-none">
                                            <button
                                                type="button"
                                                onClick={handleNextClick}
                                                className="mb-0 ml-4 h5 after:bg-gray-900 border-b-in"
                                                onMouseEnter={handleNextHover}
                                                onMouseLeave={handleNextLeave}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="overflow-hidden">
                <div className="overflow-visible mx-auto md:w-full md:grid! md:grid-cols-14">
                    {pages.length === 1 && (
                        <div className="md:col-span-12 md:col-start-2 2xl:col-start-3 2xl:col-span-10">
                            <Slide page={pages[0]} />
                        </div>
                    )}
                    {pages && pages.length > 1 && (
                        <div className="overflow-visible md:-mx-2 md:col-span-14">
                            <Slider ref={(c) => setSlider(c)} {...getConfigurableProps()}>
                                {getSlides()}
                            </Slider>
                        </div>
                    )}
                </div>
            </div>
            {button && !hideSlideshowButton && (
                <div className="mx-6 max-w-container mt-4 md:hidden">
                    <Button button={button} buttonType="outline-dark" />
                </div>
            )}
        </div>
    );
};

export default Slideshow;
